import styled from '@emotion/styled';
import { Box } from '@mui/material';

// Got to add a borderBottom of 1 px to avoid a strange white border on print

export const BaseLayout = styled.div`
  margin: 0;
  padding: 0;
  box-sizing: content-box;
  background-color: white;
  page-break-before: page;
  font-family: 'Causten', sans-serif;
`;

export const CoverLayout = styled(BaseLayout)`
  background: linear-gradient(
    to top,
    black 0%,
    black 50%,
    white 50%,
    white 100%
  );
  width: ${({ orientation }: PageLayoutProps) =>
    orientation === 'landscape' ? '297mm' : '210mm'};
  height: ${({ orientation }: PageLayoutProps) =>
    orientation === 'landscape' ? '210mm' : '297mm'};
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;
export type PageLayoutProps = {
  orientation?: 'landscape' | 'portrait';
};
export const PageLayout = styled(BaseLayout)`
  width: ${({ orientation }: PageLayoutProps) =>
    orientation === 'landscape' ? '297mm' : '210mm'};
  height: ${({ orientation }: PageLayoutProps) =>
    orientation === 'landscape' ? '210mm' : '297mm'};
  max-height: ${({ orientation }: PageLayoutProps) =>
    orientation === 'landscape' ? '210mm' : '297mm'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

export const CoverContentLayout = styled.div`
  background-color: var(--pdf-green);
  min-height: 277mm;
  display: flex;
  flex-direction: column;
  position: relative;
`;

export const CoverLayoutBorders = styled.div`
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 8px;
    height: 40%;
    background-color: var(--pdf-green);
  }

  &::before {
    top: 30%; /* Centers the border vertically */
    left: -4px; /* Half inside, half outside */
  }

  &::after {
    top: 30%;
    right: -4px; /* Half inside, half outside */
  }
`;

export const ContentLayout = styled.div`
  position: relative;
  box-sizing: border-box;
  border-right: 1cm solid var(--brightergreen);
  width: 90%;
  height: 100%;
  background-color: white;
  min-height: 95%;
  display: flex;
  flex-direction: column;
  align-items: baseline;
`;
export const ContentLayoutBorders = styled.div`
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 8px;
    height: 40%;
    background-color: var(--pdf-green);
  }

  &::before {
    top: 45%; /* Centers the border vertically */
    left: -4px; /* Half inside, half outside */
  }

  &::after {
    top: 10%;
    right: -4px; /* Half inside, half outside */
  }
`;

export const Logo = styled.img`
  max-width: 80px;
  max-height: 40px;
  cursor: pointer;
`;

export type TextProps = {
  fontSize?: string;
  color?: string;
  condensed?: boolean;
  backgroundColor?: string;
  fontWeight?: string;
  textAlign?: string;
  fontStyle?: string;
  fontFamily?: string;
  textTransform?: string;
  whiteSpace?: string;
  width?: string;
};

export const Text = styled.div`
  font-size: ${({ fontSize }: TextProps) => fontSize || '1rem'};
  font-weight: ${({ fontWeight }: TextProps) => fontWeight || 'lighter !important'};
  color: ${({ color }: TextProps) => color || 'black'};
  line-height: ${({ condensed }: TextProps) => (condensed ? '0.85' : '1')};
  text-align: ${({ textAlign }: TextProps) => textAlign || 'center'};
  text-wrap: wrap;
  font-style: ${({ fontStyle }: TextProps) => fontStyle || 'normal'};
  text-transform: ${({ textTransform }: TextProps) => textTransform || 'none'};
  white-space: ${({ whiteSpace }: TextProps) => whiteSpace || 'normal'};
  width: ${({ width }: TextProps) => width || 'auto'};
`;

export const Paragraph = styled(Text)`
  padding: 10mm 5mm;

  text-align: justify;
`;

export const SubTitle = styled(Text)`
  position: relative;
  display: inline-block;
  padding: 10px 30px 10px 60px;
  font-family: 'Causten', sans-serif;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
  color: white;
  background-color: black;
  clip-path: polygon(
    0% 100%,
    21px 0%,
    30px 0%,
    9px 100%,
    15px 100%,
    36px 0%,
    45px 0%,
    24px 100%,
    30px 100%,
    51px 0%,
    100% 0%,
    calc(100% - 21px) 100%
  );
`;

export const SubTitlePagination = styled(Text)`
  position: relative;
  display: inline-block;
  font-family: 'Causten', sans-serif;
  padding: 10px 60px 10px 30px;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
  color: white;
  background-color: black;
  width: 110px;
  clip-path: polygon(
    0% 100%,
    21px 0%,
    calc(100% - 30px) 0%,
    calc(100% - 51px) 100%,
    calc(100% - 45px) 100%,
    calc(100% - 24px) 0%,
    calc(100% - 15px) 0%,
    calc(100% - 36px) 100%,
    calc(100% - 30px) 100%,
    calc(100% - 9px) 0%,
    100% 0%,
    calc(100% - 21px) 100%
  );
`;

type GraphContainerProps = {
  width?: string;
  maxHeight?: string;
  padding?: string;
  flexDirection?: string;
};
export const GraphContainer = styled(Box)`
  display: flex;
  flex-direction: ${({ flexDirection }: GraphContainerProps) =>
    flexDirection || 'column'};
  justify-content: top;
  align-items: flex-start;
  padding: ${({ padding }: GraphContainerProps) => padding || '10mm 10mm'};
  background-color: var(--pdf-lightgreen);
  width: ${({ width }: GraphContainerProps) => width || '100%'};
  max-height: ${({ maxHeight }: GraphContainerProps) => maxHeight || '100%'};
`;

export const GraphSubtitle = styled(Text)`
  font-size: ${({ fontSize }: TextProps) => fontSize || '1rem'};
  font-weight: bold;
  margin-bottom: 10px;
`;

export const Title = styled.h1`
  color: var(--darkblue);
  border-left: 6px solid var(--darkgreen);
  padding-left: 8px;
  font-size: 36px;
  line-height: 1;
  align-self: flex-start;
`;

export const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: absolute;
  bottom: 1rem;
  width: 100%;
`;